import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { organizationsAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, ArrowLeft, Loader2, Save } from 'lucide-react';

const ORG_ROLES = [
  { id: 'client', label: 'Client' },
  { id: 'venue_owner', label: 'Venue Owner' },
  { id: 'vendor', label: 'Vendor' },
  { id: 'partner', label: 'Partner' },
];

const OrganizationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isViewer) { navigate('/organizations'); return; }
  }, [isViewer, navigate]);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({
    organization_name: '',
    org_roles: [],
    billing_terms: '',
    billing_notes: '',
    account_manager_id: '',
    client_intelligence: '',
    lessons_learned: '',
  });

  useEffect(() => {
    loadContacts();
    if (isEditing) {
      loadOrganization();
    }
  }, [id]);

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.list({ limit: 1000 });
      // Handle paginated response format
      setContacts(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  };

  const loadOrganization = async () => {
    try {
      const response = await organizationsAPI.get(id);
      setForm({
        organization_name: response.data.organization_name || '',
        org_roles: response.data.org_roles || [],
        billing_terms: response.data.billing_terms || '',
        billing_notes: response.data.billing_notes || '',
        account_manager_id: response.data.account_manager_id || '',
        client_intelligence: response.data.client_intelligence || '',
        lessons_learned: response.data.lessons_learned || '',
      });
    } catch (error) {
      toast.error('Failed to load organization');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setForm((prev) => ({
      ...prev,
      org_roles: prev.org_roles.includes(roleId)
        ? prev.org_roles.filter((r) => r !== roleId)
        : [...prev.org_roles, roleId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.organization_name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await organizationsAPI.update(id, form);
        toast.success('Organization updated');
      } else {
        const response = await organizationsAPI.create(form);
        toast.success('Organization created');
        navigate(`/organizations/${response.data.id}`);
        return;
      }
      navigate(`/organizations/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organization-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditing ? `/organizations/${id}` : '/organizations'}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            {isEditing ? 'Edit Organization' : 'New Organization'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organization_name">Organization Name *</Label>
              <Input
                id="organization_name"
                value={form.organization_name}
                onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                placeholder="Enter organization name"
                className="bg-background/50"
                data-testid="org-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Organization Roles</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {ORG_ROLES.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={role.id}
                      checked={form.org_roles.includes(role.id)}
                      onCheckedChange={() => handleRoleToggle(role.id)}
                      data-testid={`role-${role.id}`}
                    />
                    <Label htmlFor={role.id} className="cursor-pointer">
                      {role.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_manager_id">Account Manager</Label>
              <Select
                value={form.account_manager_id || 'none'}
                onValueChange={(value) => setForm({ ...form, account_manager_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="bg-background/50" data-testid="account-manager-select">
                  <SelectValue placeholder="Select account manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No account manager</SelectItem>
                  {contacts
                    .filter(contact => contact.role_tags && contact.role_tags.includes('AVF Staff'))
                    .map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.contact_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Billing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="billing_terms">Billing Terms</Label>
              <Input
                id="billing_terms"
                value={form.billing_terms}
                onChange={(e) => setForm({ ...form, billing_terms: e.target.value })}
                placeholder="e.g., Net 30, Prepay"
                className="bg-background/50"
                data-testid="billing-terms-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_notes">Billing Notes</Label>
              <Textarea
                id="billing_notes"
                value={form.billing_notes}
                onChange={(e) => setForm({ ...form, billing_notes: e.target.value })}
                placeholder="Special billing instructions, PO requirements, etc."
                className="bg-background/50 min-h-[100px]"
                data-testid="billing-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Intelligence & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client_intelligence">Client / Corporate Intelligence</Label>
              <Textarea
                id="client_intelligence"
                value={form.client_intelligence}
                onChange={(e) => setForm({ ...form, client_intelligence: e.target.value })}
                placeholder="Internal politics, approval processes, production expectations..."
                className="bg-background/50 min-h-[120px]"
                data-testid="client-intelligence-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lessons_learned">Lessons Learned</Label>
              <Textarea
                id="lessons_learned"
                value={form.lessons_learned}
                onChange={(e) => setForm({ ...form, lessons_learned: e.target.value })}
                placeholder="What worked, what didn't, things to remember..."
                className="bg-background/50 min-h-[120px]"
                data-testid="lessons-learned-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            asChild
          >
            <Link to={isEditing ? `/organizations/${id}` : '/organizations'}>
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-org-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Update' : 'Create'} Organization
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationForm;
